import { lazy, ReactElement, Suspense } from 'react';

import { Navigate, RouteObject } from 'react-router-dom';

import { ModalTrobber } from 'helpers/modal/Trobber';
import { PageLayout } from 'helpers/pageLayout';
import { sentryCreateBrowserRouter } from 'config/sentry';

const ArchiveCard = lazy(() => import('views/event/item'));
const ArchiveEventsList = lazy(() => import('views/archive/list'));
const Calendar = lazy(() => import('views/calendar'));
const GuestsInvitation = lazy(() => import('views/event/GuestsInvitation'));
const EventEditing = lazy(() => import('views/event/editing'));
const EventList = lazy(() => import('views/event/list'));
const LocationList = lazy(() => import('views/locations/list'));
const GuestsList = lazy(() => import('views/guests/list'));
const HallTemplatesCreate = lazy(() => import('views/hallTemplates/create'));
const HallTemplatesEdit = lazy(() => import('views/hallTemplates/edit'));
const HallTemplatesList = lazy(() => import('views/hallTemplates/list'));
const TicketsList = lazy(() => import('views/tickets/List'));
const TicketsEventsList = lazy(() => import('views/tickets/EventsList'));
const UserDetails = lazy(() => import('views/users/Item'));
const UsersList = lazy(() => import('views/users/list'));
const EventCreateVista = lazy(() => import('views/event/editing/vista'));
const TagsList = lazy(() => import('views/tags/list'));

/**
 * Общий Layout компонент
 */
const AppLayout = (): ReactElement => (
  <Suspense fallback={<ModalTrobber />}>
    <PageLayout withSideBar={true} isAuth={false} />
  </Suspense>
);

// Маршруты seat manager
const managerSeatRoutes: RouteObject[] = [
  {
    path: '/',
    element: <EventList />
  },
  {
    path: '/events',
    children: [
      {
        path: '',
        element: <EventList />
      },
      {
        path: ':id/guests-invitation',
        element: <GuestsInvitation />
      }
    ]
  },
  {
    path: '/archive',
    children: [
      {
        path: '',
        element: <ArchiveEventsList />
      },
      {
        path: ':id',
        element: <ArchiveCard />
      }
    ]
  },
  {
    path: '/tickets',
    children: [
      {
        path: '',
        element: <TicketsEventsList />
      }, {
        path: 'event/:eventId',
        element: <TicketsList />
      }
    ]
  },
  {
    path: '/calendar',
    children: [
      {
        path: '',
        element: <Calendar />
      }
    ]
  }
];

// Маршруты event manager
const managerEventRoutes: RouteObject[] = [
  {
    path: '/locations',
    element: <LocationList />
  },
  {
    path: '/hall-templates',
    children: [
      {
        path: '',
        element: <HallTemplatesList />
      },
      {
        path: 'create',
        element: <HallTemplatesCreate />
      },
      {
        path: 'edit',
        children: [
          {
            path: ':id',
            element: <HallTemplatesEdit />
          }
        ]
      }
    ]
  },
  {
    path: '/events',
    children: [
      {
        path: 'create',
        children: [
          {
            path: '',
            element: <EventEditing />
          },
          {
            path: 'vista',
            element: <EventCreateVista />
          }
        ]
      },
      {
        path: 'edit/:id',
        element: <EventEditing />
      },
      {
        path: 'edit/vista/:id',
        element: <EventCreateVista />
      }
    ]
  },
  ...managerSeatRoutes
];

// Маршруты администратора
const adminRoutes: RouteObject[] = [
  {
    path: '/guests',
    element: <GuestsList />
  },
  {
    path: 'users',
    children: [
      {
        path: '',
        element: <UsersList />
      },
      {
        path: ':id',
        element: <UserDetails />
      }
    ]
  },
  {
    path: 'tags',
    children: [
      {
        path: '',
        element: <TagsList />
      }
    ]
  },
  ...managerEventRoutes
];

/**
 * Функция для создания роутов на основе роли
 * @param role - роль
 */
const createRoutes = (role: string): RouteObject[] => {
  let routes: RouteObject[];

  switch (role) {
    case 'admin':
      routes = adminRoutes;
      break;
    case 'managerEvent':
      routes = managerEventRoutes;
      break;
    default:
      routes = managerSeatRoutes;
      break;
  }

  return [
    {
      element: <AppLayout />,
      children: routes
    },
    {
      path: '*',
      element: <Navigate to='/' />
    }
  ];
};

// Маршруты координатора мероприятий (те же что у seat manager + редактирование мероприятия)
const coordinatorEventRoutes: RouteObject[] = [
  ...managerSeatRoutes,
  {
    path: '/events',
    children: [
      {
        path: 'edit/:id',
        element: <EventEditing />
      }
    ]
  }
];

// Создание роутеров для каждого типа пользователя
export const routerAdmin = sentryCreateBrowserRouter(createRoutes('admin'));
export const routerManagerEvent = sentryCreateBrowserRouter(createRoutes('managerEvent'));
export const routerManagerSeat = sentryCreateBrowserRouter(createRoutes('managerSeat'));
export const routerCoordinatorEvent = sentryCreateBrowserRouter([
  {
    element: <AppLayout />,
    children: coordinatorEventRoutes
  },
  {
    path: '*',
    element: <Navigate to='/' />
  }
]);
